import React, {
    forwardRef,
    MutableRefObject,
    ReactNode,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import './send-notification-editor.scss'
import { useJourneyContext } from '../context'
import { parseNotificationResponse } from '../../notifications/helpers/notification-helpers'

import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import { NotificationBuilderRef } from '../../../components/notification-builder/notification-builder'
import { NotificationBuilderLevel } from '../../../components/notification-builder/enums'
import type { INotificationBuilderState } from '../../../components/notification-builder/interfaces/notification-builder-state.interface'
import {
    buildSubmittedNotificationPackages,
    validateSubmit as validateNotificationBuilder,
} from '../../../components/notification-builder/helpers'
import {
    EditableNode,
    getItemStrategy,
    getSendParams,
    isSendWithItemStrategyActionStep,
    JourneyNode,
    NodeEditorRef,
} from '../types/journey-nodes'
import type { IActionStep } from '@pushly/models/lib/structs/journeys'
import { getJourneyTriggerItemType } from '../../../_utils/journeys'
import { useItemStrategyOptions } from '../hooks/use-item-strategy-options'
import type { JourneyNotificationBuilderConfig } from './types'
import { MacroType } from '@pushly/aqe/lib/components'
import { DomainNotificationBuilderView } from '../../../components/notification-builder/views/domain-notification-builder-view'
import { JourneyBuilderMode } from '../enums'
import { handleNodeParamsUpdate } from '../hooks/use-submit-action'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { NotificationCatalogItemPickProps } from '../../../components/notification-builder/views/internals'

type SendNotificationEditorProps = {
    ref?: MutableRefObject<NodeEditorRef>
    node: JourneyNode<IActionStep>
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    imageMacroToggleLabel?: string
    imageMacroValue?: string
    imageMacroIcon?: ReactNode
    builderProps?: Partial<JourneyNotificationBuilderConfig>
}

export const SendNotificationEditor = forwardRef<NodeEditorRef, SendNotificationEditorProps>((props, ref) => {
    const { node } = props

    const [state, _dispatch] = useJourneyContext()

    const appState = useService(AppState)

    const journey = state.journey
    const domain = state.domain
    const flags = appState.flags

    const action = node.data.step

    const sendParams = getSendParams(action)
    const notification = sendParams?.notification

    const hasItemStrategy = isSendWithItemStrategyActionStep(action)
    const currentSelectedStrategy = hasItemStrategy && journey ? getSendParams(action)?.item_strategy : undefined

    // For trigger-based campaigns, get itemType from trigger; for scheduled, use domain's ecommConfig
    const itemType =
        hasItemStrategy && journey ? getJourneyTriggerItemType(journey) ?? domain?.ecommConfig?.itemType : undefined
    const itemStrategy = useItemStrategyOptions({
        action,
        itemType,
        allNodes: state.nodes,
        currentNodeId: node.id,
        currentSelectedStrategy,
    })

    const builderConfig: JourneyNotificationBuilderConfig = useMemo(() => {
        let parsedNotifications: JourneyNotificationBuilderConfig['notifications']
        if (notification) {
            // plainToInstance (inside parseNotificationResponse) reads data
            // properties from any object — struct instances work fine as input,
            // prototype methods are ignored. No serialization needed.
            const parsed = parseNotificationResponse(notification)

            parsedNotifications = [parsed]
        }

        const config: JourneyNotificationBuilderConfig = {
            domain,
            level: NotificationBuilderLevel.CAMPAIGN,
            mode: notification ? 'edit' : 'create',
            notifications: parsedNotifications,
            defaultChannels: state.journey?.channels,
            defaultSelectedNotifId: sendParams?.notification_id ? Number(sendParams.notification_id) : undefined,
            showWebhookOptions: false,
        }

        if (itemStrategy.isCatalog) {
            config.catalogItemType = itemType ?? undefined
            config.catalogItemPickStrategyOptions = itemStrategy.options
            config.selectedCatalogItemPickStrategy = itemStrategy.selected

            const existingStrategy = getItemStrategy(node.data.step)
            if (existingStrategy) {
                config.selectedCatalogItemPickStrategy = existingStrategy
            }

            config.macroProps = {
                categories: {
                    urlFields: ['domain', 'device', 'location', 'ecomm', 'notification', 'secrets', 'custom'],
                    messageFields: ['location', 'ecomm', 'secrets', 'custom'],
                },
                imageMacroProps: {
                    label: itemStrategy.templates.imageMacroTagLabel,
                    tag: itemStrategy.templates.imageMacroTag,
                },
            }

            config.onInitialVariantInit = (variant) => {
                const contentModel = variant.getContent().getDefaultContent()
                contentModel.setLandingUrl(itemStrategy.templates.url)
                contentModel.setTitle(itemStrategy.templates.title)
                contentModel.setBody(itemStrategy.templates.body)
                contentModel.setImageUrl(itemStrategy.templates.image)
            }
        }

        return config
    }, [itemStrategy, itemType, notification, state.journey?.channels, domain])

    // Show catalog item options if we have a catalog item type OR if we have item strategy options
    const isCatalogItemNotification =
        !!builderConfig.catalogItemType || (itemStrategy.isCatalog && itemStrategy.options.length > 0)
    if (!domain) return <></>

    const notificationType = isCatalogItemNotification ? 'cart_notification' : 'standard_notification'

    const catalogItemPick: NotificationCatalogItemPickProps | false = useMemo(() => {
        if (!isCatalogItemNotification) return false

        return {
            options: builderConfig.catalogItemPickStrategyOptions ?? [],
            itemType,
            defaultSelectedOption: builderConfig.selectedCatalogItemPickStrategy,
        } satisfies NotificationCatalogItemPickProps
    }, [builderConfig, isCatalogItemNotification])

    const builderRef = useRef<NotificationBuilderRef>(null!)

    // Track whether the user has made any edits to the notification.
    // Snapshot the builder's variant state after initialization completes,
    // then compare at submit time. Same type, same construction path.
    const initialVariantSnapshotRef = useRef<string | null>(null)

    const onSubmit = useCallback<() => Promise<any>>(async () => {
        if (!builderRef.current) {
            console.warn('Builder ref is not initialized, skipping submit action')
            return
        }
        const builderState = builderRef.current.state

        if (state.mode === JourneyBuilderMode.READONLY) {
            return
        }

        const valid = await validateNotificationBuilder(builderState, domain, flags, false, false)
        if (!valid) {
            return
        }

        const { notifications } = buildSubmittedNotificationPackages(domain, builderState, flags)

        if (!notifications.length) {
            return
        }

        const nextItemStrategy = isCatalogItemNotification ? builderState.selectedCatalogItemPickStrategy : undefined
        const notificationParam = notifications[0]

        // If the builder hasn't been touched, preserve the existing notification_id.
        // Compare the current variant state against the snapshot taken after init.
        const currentVariantSnapshot = JSON.stringify(
            builderState.variants.map(v => v.serialize()),
        )
        const userEdited = initialVariantSnapshotRef.current !== null
            && currentVariantSnapshot !== initialVariantSnapshotRef.current
        if (!userEdited && sendParams?.notification_id) {
            if (nextItemStrategy !== undefined) {
                return handleNodeParamsUpdate(node, stripUndefined({ item_strategy: nextItemStrategy }))
            }
            return
        }

        // User edited the notification — send the new payload and clear
        // notification_id so the inline payload survives journey-wizard.tsx's
        // dedup logic (which strips `notification` when both fields are present).
        return handleNodeParamsUpdate(
            node,
            stripUndefined({
                notification: notificationParam,
                item_strategy: nextItemStrategy,
            }),
            { clearNotificationId: !!sendParams?.notification_id },
        )
    }, [
        domain,
        flags,
        isCatalogItemNotification,
        node,
        state.mode,
        validateNotificationBuilder,
        buildSubmittedNotificationPackages,
    ])

    useImperativeHandle(ref, () => ({ onSubmit }))

    const builderProps = {
        ...builderConfig,
        ...props.builderProps,
        onChange: (builderState: INotificationBuilderState) => {
            // Snapshot the variant state once the builder finishes loading.
            // At submit time, we compare the current variant state against
            // this snapshot to detect user edits.
            if (initialVariantSnapshotRef.current === null
                && !builderState.loading
                && builderState.initialNotificationStateLoaded) {
                initialVariantSnapshotRef.current = JSON.stringify(
                    builderState.variants.map(v => v.serialize()),
                )
            }
        },
    }

    return (
        <div id="send-notification-editor">
            <DomainNotificationBuilderView
                ref={builderRef}
                formType="send"
                formMode="create"
                journey={journey}
                journeyNotificationType={notificationType}
                isInDrawer={true}
                catalogItemPick={catalogItemPick}
                hidePageHeader={true}
                hidePushlyAiToggle={true}
                hidePageAnalysisToggle={true}
                hideAudienceSelection={true}
                hideEstimatedReach={true}
                hideDeliveryChannels={true}
                hideScheduling={true}
                hideSubmit={true}
                onSubmit={noop}
                onCancel={noop}
                builderProps={{
                    ...builderProps,
                    macroProps: builderProps.macroProps,
                }}
            />
        </div>
    )
})
